import { _ } from "../../core/polyglot/polyglot.js";
import { day } from "../../models/period/PeriodType.js";
import { htmlCanvas } from "@foretagsplatsen/widgetjs";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import accountingDocument from "../shared/accountingDocument.js";
import BusinessActivity from "../../models/accounting/BusinessActivity.js";
import BusinessActivityChartWidget from "./BusinessActivityChartWidget.js";
import BusinessActivityEvent from "../../models/accounting/BusinessActivityEvent.js";
import BusinessActivityWidget from "./BusinessActivityWidget.js";
import dimensionPickerWidget from "../../core/widgets/dimensionPickerWidget.js";
import PeriodTypePicker from "../shared/PeriodTypePicker/PeriodTypePicker.js";
import regionWidget from "../../core/widgets/regionWidget.js";
import session from "../../core/session.js";
import SwitchButton from "../../core/widgets/SwitchButton/SwitchButton.js";

/**
 * A document that displays a chart of the current selected business period, a summary chart and a table
 * with business activities.
 */
function businessActivityDocument(spec, my) {
	spec ||= {};
	my ||= {};

	const that = accountingDocument(spec, my);
	const currentUser = session.getCurrentUser();
	const currentCompany = session.getCompany();

	my.periodType = day;

	my.isLimited = !currentUser.hasCompanyRole(
		["normal"],
		currentCompany.getLegacyId(),
	);

	that.title = _("#business-activity-document");

	that.documentId = "business-activity-document";

	that.help = function () {
		return [
			{
				title: _("Business activity"),
				content: _(
					"This page visualizes how individual day and even single business activities adds up and effect the Profit or Loss of a period and eventually the profit or loss for and entire fiscal year. The information on this page can be filtered business objects as project and or cost centers for example.",
				),
			},
			{
				title: _("#business-activity-section"),
				content: _(
					"By clicking on a single bar in the chart a table will appear below the graph listing all business activity that are effecting the Profit/Loss for that day. Clicking on a specific transaction number or an account number will open these in a new window.",
				),
				element: "business-chart",
			},
		];
	};

	let selectedPeriodTitle = null;
	const antiForgeryToken = session.getAntiForgeryToken();

	my.businessActivityEventRepository = makeMonitorRepository({
		route: "/Accounting2/Company/#companyId/BusinessPeriodEvent/#periodType",
		model: BusinessActivityEvent,
		defaultFilters: { companyId: session.getCompany().getLegacyId() },
		antiForgeryToken,
	});

	my.businessActivityRepository = makeMonitorRepository({
		route: "/Accounting2/Company/#companyId/BusinessPeriod/#period/Interval/#periodType",
		model: BusinessActivity,
		defaultFilters: { companyId: session.getCompany().getLegacyId() },
		antiForgeryToken,
	});

	my.businessTitle = regionWidget({
		content: function (html) {
			html.render(my.renderBusinessActivityTitleOn);
		},
	});

	my.periodTypePicker = PeriodTypePicker.forSubTypes({
		selectedPeriodTypeId: my.periodType.getId(),
		onChange: (periodType) => {
			my.periodType = periodType;
			that.showCurrentPeriod();
		},
	});

	my.showCreditedRecordsCheckBox = new SwitchButton();

	my.showCreditedRecordsCheckBox.stateChanged.register(() =>
		that.showCurrentPeriod(),
	);

	my.dimensionPicker = dimensionPickerWidget({
		dimensionTree: session.getDimensionTree(),
		dataTestName: "pick-dimension",
	});

	my.dimensionPicker.onDimensionSelected.register(() => {
		that.showCurrentPeriod();
	});

	session.onPeriodChange((period) => {
		my.dimensionPicker.setDimensionTree(
			period.getYear().getDimensionTree(),
		);
	});

	that.actions = [
		dimensionObjectPickerRenderer(),
		periodTypePickerRenderer(),
		showCreditedRecordsCheckBoxRenderer(),
	];

	// Bottom table
	my.businessActivityEvent = new BusinessActivityWidget();

	// Top chart
	my.businessActivityChart = new BusinessActivityChartWidget({
		isLimited: my.isLimited,
		shouldHideAppropriations: shouldHideAppropriations(),
	});

	my.businessActivityChart.onClickPoint.register(selectedChartEntity);
	my.businessActivityChart.onClickBand.register(selectedChartEntity);

	that.periodChanged = function (callback) {
		my.businessActivityEvent.setEmptySource();
		callback();
	};

	that.showCurrentPeriod = async function () {
		that.showUpdating();

		const period = session.getPeriod();
		const showCreditedRecords = my.showCreditedRecordsCheckBox.getState();

		my.dimensionPicker.setDimensionTree(
			period.getYear().getDimensionTree(),
		);

		createSubTitle(); // NB! We have to create the sub title AFTER the period picker is initialized

		const businessActivity = await my.businessActivityRepository.get({
			filter: {
				periodType: my.periodType.getId(),
				period: period.toString(),
				dimensionIdentifier:
					my.dimensionPicker.getSelectedDimensionId(),
				showCreditedRecords,
			},
		});

		my.businessActivityChart.show({
			businessActivity,
		});

		my.businessActivityChart.update();
		that.showContent();

		my.businessActivityEvent.update();
		my.businessTitle.update();
	};

	my.renderContentOn = function (html) {
		my.renderBusinessActivityChartOn(html);
		my.renderBusinessActivityEventOn(html);
	};

	my.renderBusinessActivityChartOn = function (html) {
		html.render(
			my.section({
				id: "business-chart",
				dataHelpName: "business-chart",
				content: [my.businessTitle, my.businessActivityChart],
			}),
		);
	};

	my.renderBusinessActivityTitleOn = function (html) {
		html.h2(
			_("Income and cost per {periodType} {date}", {
				date: session.getPeriod().toSmartString(),
				periodType: my.periodType.getName().toLowerCase(),
			}),
		);
	};

	my.renderBusinessActivityEventOn = function (html) {
		if (my.isLimited) {
			return;
		}

		html.render(
			my.section({
				id: "business-activity-table",
				content: function (html) {
					selectedPeriodTitle = html.h2(
						my.selectedPeriodTitleRenderer(),
					);

					html.render(my.businessActivityEvent);
				},
			}),
		);
	};

	my.selectedPeriodTitleRenderer = function (selectedPeriod) {
		return function (html) {
			html.span(_("#business-activity-section"));
			if (selectedPeriod) {
				html.small(" ", selectedPeriod.toSmartString());
			}
		};
	};

	function dimensionObjectPickerRenderer() {
		return my.rightMenuSection(
			_("Show an object or all business activity"),
			my.dimensionPicker,
		);
	}

	function periodTypePickerRenderer() {
		return my.rightMenuSection(
			_("Divide the period in"),
			my.periodTypePicker,
		);
	}

	function showCreditedRecordsCheckBoxRenderer() {
		return my.rightMenuSection("", (html) => {
			html.div(
				{ klass: "right-menu-block" },
				html.span({ klass: "switch-label" }, _("Credit records")),
				my.showCreditedRecordsCheckBox,
			);
		});
	}

	my.updateSelectedPeriodTitle = function (period) {
		if (!selectedPeriodTitle) {
			return;
		}
		selectedPeriodTitle.asJQuery().empty();

		htmlCanvas(selectedPeriodTitle.asJQuery()).render(
			my.selectedPeriodTitleRenderer(period),
		);
	};

	my.willDetach = function () {
		my.dimensionPicker.willDetach();
	};

	function selectedChartEntity(entity) {
		if (my.isLimited) {
			return;
		}
		const selectedPeriod = entity.options.period;
		const dimensionIdentifier = my.dimensionPicker.getSelectedDimensionId();
		my.updateSelectedPeriodTitle(selectedPeriod);

		my.businessActivityEvent.showEvents({
			businessEvents: my.businessActivityEventRepository.get({
				filter: {
					periodType: selectedPeriod.toString() || "",
					dimensionIdentifier,
				},
			}),
			dimensionIdentifier,
		});
	}

	function createSubTitle() {
		that.subtitle = _(
			"Activity {periodType}-by-{periodType} for {project} on {date}",
			{
				periodType: my.periodType.getName().toString().toLowerCase(),
				project: my.dimensionPicker
					.getSelectedDimensionName()
					.toString()
					.toLowerCase(),
				date: session.getPeriod().toSmartString(),
			},
		);
	}

	function shouldHideAppropriations() {
		const countrySettings = session.getCountrySettings();
		return countrySettings && countrySettings.shouldHideAppropriations();
	}

	return that;
}

export default businessActivityDocument;
